import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Result, Select, Space, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@services/api';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { apiErrorMessage } from '@utils/apiError';
import type { PmPlatform, PmQuery, ProductMetricsResponse } from '@/types';
import { dayLabel } from '../bq-insights/bqMath';
import { istToday } from '../ftue-progression/ftueQuery';
import { ProductMetricsBoard } from './ProductMetricsBoard';
import { PM_MIN_DATE, versionOptions } from './pmFormat';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const DAY_FORMAT = 'YYYY-MM-DD';

const PLATFORMS: Array<{ value: PmPlatform; label: string }> = [
  { value: 'all', label: 'All platforms' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const disabledDate = (d: Dayjs) => d.isBefore(PM_MIN_DATE, 'day') || d.isAfter(istToday(), 'day');

/**
 * DAU, first-time users, engagement, the stump reward funnel, match engagement per mode, overs
 * per player and D1/D3 retention, for a date window, platform and app version. The first request
 * has no params, so the server picks its default (the last 7 days, every version and platform)
 * and the controls then show the filter it used. The rollups are rebuilt every 4 hours and the
 * backend caches each answer, so this page does not poll.
 */
export const ProductMetricsPage: React.FC = () => {
  const [data, setData] = useState<ProductMetricsResponse | null>(null);
  const [query, setQuery] = useState<PmQuery>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (q: PmQuery) => {
      const isLatest = beginRequest();
      setLoading(true);
      try {
        const res = await adminApi.getProductMetrics(q);
        if (!isLatest()) return;
        setData(res.data);
        setQuery(res.data.filter);
        setFailure(null);
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to load Product Metrics', err);
        setFailure(apiErrorMessage(err, 'Failed to load Product Metrics.'));
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [beginRequest]
  );

  useEffect(() => {
    void fetchData({});
  }, [fetchData]);

  const change = (patch: PmQuery) => {
    const next = { ...query, ...patch };
    setQuery(next);
    void fetchData(next);
  };

  if (!data) {
    if (failure) {
      return (
        <Result
          status="error"
          title="Couldn't load Product Metrics"
          subTitle={failure}
          extra={
            <Button type="primary" onClick={() => void fetchData(query)} loading={loading}>
              Retry
            </Button>
          }
        />
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const from = query.from ?? data.filter.from;
  const to = query.to ?? data.filter.to;
  const version = query.version ?? data.filter.version;
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          Product Metrics
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Data through {data.dataThrough ? dayLabel(data.dataThrough) : '—'} · updated every 4 hours
          · GA4 (Android; iOS is not in the export yet) · days are IST
        </Text>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <Space wrap>
          <RangePicker
            aria-label="Dates"
            allowClear={false}
            value={[dayjs(from), dayjs(to)]}
            disabledDate={disabledDate}
            onChange={(range) => {
              if (range && range[0] && range[1]) {
                change({ from: range[0].format(DAY_FORMAT), to: range[1].format(DAY_FORMAT) });
              }
            }}
          />
          <Select
            aria-label="Platform"
            style={{ minWidth: 140 }}
            value={query.platform ?? data.filter.platform}
            options={PLATFORMS}
            onChange={(platform: PmPlatform) => change({ platform })}
          />
          <Select
            aria-label="App version"
            style={{ minWidth: 240 }}
            showSearch
            optionFilterProp="label"
            value={version}
            options={versionOptions(data.versions, version)}
            onChange={(v: string) => change({ version: v })}
          />
        </Space>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => void fetchData(query)}
        >
          Refresh
        </Button>
      </div>
      {failure && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Couldn't load that filter"
          description={`${failure} The figures below are still for ${dayLabel(data.filter.from)} – ${dayLabel(
            data.filter.to
          )}.`}
          action={
            <Button size="small" onClick={() => void fetchData(query)} loading={loading}>
              Retry
            </Button>
          }
        />
      )}
      <Spin spinning={loading}>
        <ProductMetricsBoard data={data} />
      </Spin>
    </div>
  );
};
